import type * as hast from 'hast';

import { BLOCK_LIST_ITEM, BLOCK_LIST_OL, BLOCK_LIST_TASKS, BLOCK_LIST_UL } from '@gitbook/doc-core';
import { Block } from '@gitbook/slate';

import type { HastToGastTransformFn, HastToGastTransformMap } from './types';

export const transformBlockLists: HastToGastTransformMap = {
    ul: createListsTransform(BLOCK_LIST_UL),
    ol: createListsTransform(BLOCK_LIST_OL),
    li: async (node: hast.Element, eat, context) => {
        const [checked, children] = extractCheckbox(node);

        return [
            Block.create({
                type: BLOCK_LIST_ITEM,
                nodes: await eat('blocks', children, context),
                data:
                    typeof checked === 'boolean'
                        ? {
                              checked,
                          }
                        : {},
            }),
        ];
    },
};

/**
 * Create transform for lists. Will use a BLOCK_LIST_TASKS if necessary.
 */
function createListsTransform(listBlockType: string): HastToGastTransformFn {
    return async (node: hast.Element, eat, context) => {
        const nodes = await eat('blocks', node.children, context);
        const isTasks = nodes.some(
            (block) => block.type === BLOCK_LIST_ITEM && block.data.has('checked')
        );

        let data: undefined | Record<string, number>;

        if (listBlockType === BLOCK_LIST_OL) {
            const start = node.properties?.start;
            if (start) {
                data = { start: Number(start) };
            }
        }

        return [
            Block.create({
                type: isTasks ? BLOCK_LIST_TASKS : listBlockType,
                nodes,
                data,
            }),
        ];
    };
}

/**
 * Extract the checkbox for a list item.
 */
export function extractCheckbox(node: hast.Element): [boolean | null, hast.Element['children']] {
    const head = node.children[0];

    if (isCheckbox(head)) {
        const checked = Boolean(head.properties?.checked);

        return [checked, trimWhitespaces(node.children.slice(1))];
    }

    if (head && head.type === 'element' && head.tagName === 'p') {
        const checkbox = head.children[0];

        if (isCheckbox(checkbox)) {
            const checked = Boolean(checkbox.properties?.checked);

            return [
                checked,
                [
                    {
                        ...head,
                        children: trimWhitespaces(head.children.slice(1)),
                    },
                    ...node.children.slice(1),
                ],
            ];
        }
    }

    return [null, node.children];
}

/**
 * Trim the whitespaces between the checkbox and the content.
 */
function trimWhitespaces(children: hast.Element['children']): hast.Element['children'] {
    const [firstChild, ...rest] = children;
    if (firstChild && firstChild.type === 'text') {
        return [
            {
                ...firstChild,
                value: firstChild.value.trimLeft(),
            },
            ...rest,
        ];
    }

    return children;
}

/**
 * Return true if a node is a checkbox.
 */
function isCheckbox(
    node: hast.Element | hast.ElementContent | hast.Comment | hast.Text
): node is hast.Element & { tagName: 'input' } {
    return Boolean(
        node &&
            node.type === 'element' &&
            node.tagName === 'input' &&
            node.properties &&
            node.properties.type === 'checkbox'
    );
}
